import React, { useEffect, useState } from "react";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import api from "../../api/axois";
import styles from "./RoomManagement.module.css"; // Reuse admin styles for inner content
import layout from "./Payments.module.css"; // Reuse Payments layout (container/content/overlay)
import policyStyles from "./AdminPoliciesPage.module.css";
import { FaPlus } from "react-icons/fa";
import bookingStyles from "./BookingManagement.module.css"; // Reuse primary button style

const emptyPolicy = { title: "", content: "", category: "", placement: "information_card" };

const AdminPoliciesPage = () => {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [editingPolicy, setEditingPolicy] = useState(null);
    const [form, setForm] = useState(emptyPolicy);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const loadPolicies = () => {
        setLoading(true);
        api.get("policies")
            .then(res => setPolicies(res.data.policies || []))
            .catch(() => setError("Failed to load policies"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadPolicies();
    }, []);

    const openModal = (policy = null) => {
        setEditingPolicy(policy);
        setForm(policy ? { ...policy } : emptyPolicy);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingPolicy(null);
        setForm(emptyPolicy);
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
    };

    const handleSubmit = e => {
        e.preventDefault();
        const req = editingPolicy
            ? api.put(`policies/${editingPolicy.id}`, form)
            : api.post("policies", form);
        req.then(() => {
            closeModal();
            loadPolicies();
        }).catch(() => setError("Failed to save policy"));
    };

    const handleDelete = id => {
        if (!window.confirm("Delete this policy?")) return;
        api.delete(`policies/${id}`)
            .then(loadPolicies)
            .catch(() => setError("Failed to delete policy"));
    };

    return (
        <div className={layout.container}>
            <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
            <div className={layout.content}>
                <Header onMenuClick={() => setSidebarOpen(true)} />
                <div className={styles.wrapper}>
                    <div className={`${styles.box} ${policyStyles.boxBoost}`}>
                        <div className={policyStyles.topBar}>
                            <h2 className={`${styles.heading} ${policyStyles.title}`}>Manage Policies</h2>
                            <button className={bookingStyles.manualBookBtn} onClick={() => openModal()}>
                                <FaPlus className={bookingStyles.plusIcon} />
                                New Policy
                            </button>
                        </div>
                        {loading ? (
                            <div>Loading...</div>
                        ) : error ? (
                            <div className={styles.error}>{error}</div>
                        ) : (
                            <div className={styles.tableWrapper}>
                                <table style={{ width: "100%", marginTop: "2rem", borderCollapse: "collapse", minWidth: 600 }}>
                                    <thead>
                                        <tr style={{ background: "#f0f0f0" }}>
                                            <th style={{ padding: "10px", border: "1px solid #ccc" }}>Title</th>
                                            <th style={{ padding: "10px", border: "1px solid #ccc" }}>Category</th>
                                            <th style={{ padding: "10px", border: "1px solid #ccc" }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {policies.length === 0 ? (
                                            <tr>
                                                <td colSpan={3} style={{ textAlign: "center", padding: "20px" }}>No policies found.</td>
                                            </tr>
                                        ) : (
                                            policies.map(policy => (
                                                <tr key={policy.id}>
                                                    <td style={{ padding: "10px", border: "1px solid #ccc" }}>{policy.title}</td>
                                                    <td style={{ padding: "10px", border: "1px solid #ccc" }}>{policy.category}</td>
                                                    <td style={{ padding: "10px", border: "1px solid #ccc" }}>
                                                        <button className={policyStyles.saveButton} style={{ marginRight: 8 }} onClick={() => openModal(policy)}>Edit</button>
                                                        <button className={policyStyles.deleteButton} onClick={() => handleDelete(policy.id)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {modalOpen && (
                            <div className={styles.modalOverlay} onClick={closeModal}>
                                <div className={styles.modal} onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
                                    <h3>{editingPolicy ? "Edit Policy" : "Add Policy"}</h3>
                                    <form onSubmit={handleSubmit}>
                                        <div className={styles.formGroup}>
                                            <label>Title</label>
                                            <input
                                                type="text"
                                                name="title"
                                                value={form.title}
                                                onChange={handleChange}
                                                required
                                                className={policyStyles.input}
                                            />
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label>Category</label>
                                            <input
                                                type="text"
                                                name="category"
                                                value={form.category}
                                                onChange={handleChange}
                                                className={policyStyles.input}
                                            />
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label>Show this policy in</label>
                                            <select
                                                name="placement"
                                                value={form.placement || ""}
                                                onChange={handleChange}
                                                className={`${policyStyles.input} ${policyStyles.select}`}
                                            >
                                                <option value="information_card">Information Card</option>
                                                <option value="site_wide">Site-wide</option>
                                                <option value="booking_flow">Booking Flow</option>
                                                <option value="profile">User Profile</option>
                                            </select>
                                            <div className={policyStyles.help}>Choose where this policy should appear in the app’s UI.</div>
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label>Content (HTML/Markdown)</label>
                                            <textarea
                                                name="content"
                                                value={form.content}
                                                onChange={handleChange}
                                                rows={6}
                                                required
                                                className={policyStyles.input}
                                            />
                                        </div>
                                        <div style={{ marginTop: 16 }}>
                                            <button type="submit" className={policyStyles.saveButton}>
                                                {editingPolicy ? "Update" : "Create"}
                                            </button>
                                            <button type="button" className={policyStyles.deleteButton} style={{ marginLeft: 8 }} onClick={closeModal}>
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {sidebarOpen && (
                <div className={layout.overlay} onClick={() => setSidebarOpen(false)} />
            )}
        </div>
    );
};

export default AdminPoliciesPage;